type TPrimitive = string | number | bigint | boolean | symbol | null | undefined;

/** Use this to filter out non-distinct items from an array.
 * The ID must be a primitive, because this function uses === for the comparison.
 * For non-distinct elements, the first element will be returned.
 * @example
 * const input = [
 *   { id: 1, val: "one" },
 *   { id: 2, val: "two" },
 *   { id: 1, val: "uno" },
 * ];
 * const output = input.filter(distinctBy((it) => it.id)));
 * // The output array will contain the following:
 * const expectedOutput = [
 *   { id: 1, val: "one" },
 *   { id: 2, val: "two" },
 * ]; */
export function distinctBy<
  T,
  V extends TPrimitive,
>(
  idGetter: (item: T) => V,
): Parameters<Array<T>["filter"]>[0] {
  return distinctWith((it, val) => idGetter(it) === idGetter(val));
}

/** Use this to remove duplicate items from an array.
 * This is used to handle non-primitive values that need a comparator,
 * use {@link distinct} for primitive values.
 * @example
 * const input: SomeComplexObject = [ a, b, c ];
 * const output = input.filter(distinctWith((a, b) => a.equals(b)));
 * */
export function distinctWith<T>(
  comparator: (a:T, b:T) => boolean,
): Parameters<Array<T>["filter"]>[0] {
  return (val, index, arr) => arr
    // An array item is distinct if every time it is encountered is
    // also the first time it is encountered in the array.
    // If this isn't the first time it is encountered, it will be filtered out.
    .findIndex((it) => comparator(it, val)) === index;
}

/** Use this to remove duplicate primitive items from an array.
 * @example
 * const input = [ 1, 2, 5, 2, 3, 4, 5 ];
 * const output = input.filter(distinct);
 * // The output array will contain the following:
 * const expectedOutput = [ 1, 2, 5, 3, 4 ];
 * */
export function distinct<T extends TPrimitive>(
  val: T,
  index: number,
  arr: T[],
): boolean {
  return arr.findIndex((it) => it === val) === index;
}

/** Use this to check if an object is not null or undefined.
 * You can also use this to remove non-null values from an array.
 * @example
 * const input = [ 1, null, 9, undefined, 3 ];
 * const output = input.filter(isNotNull);
 * // The output array will contain the following:
 * const expectedOutput = [ 1, 9, 3 ]; */
export function isNotNull<T>(it: T): it is NonNullable<T> {
  return it !== null && it !== undefined;
}

/** Use this to find all objects in an array where a certain property is not null */
export function filterNotNullProp<T, K extends keyof T>(
  array: T[],
  property: K,
): (T & { [P in K]: NonNullable<T[P]> })[] {
  return array.filter(
    (item): item is T & {
      [P in K]: NonNullable<T[P]>
    } => isNotNull(item[property]),
  );
}

/** @return a copy of the input array with null and undefined values removed. */
export function filterNotNull<T>(array: T[]): NonNullable<T>[] {
  return array.filter(isNotNull);
}

export const getFirstOrNull = <T>(array: T[]): T | null => (array.length ? array[0] : null);
